import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArticleIcon from '@mui/icons-material/Article';
import CloseIcon from '@mui/icons-material/Close';
import { ReplaceIcon } from '../components/actionIcons';
import DeleteIconButton from '../components/DeleteIconButton';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import PublishIcon from '@mui/icons-material/Publish';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Box, Button, FormControlLabel, Grid, IconButton, Link, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useDropzone } from 'react-dropzone';

import { DataAPIContext } from '../api';
import { DELETE, UPDATE, UPLOAD } from '../api/dataProvider';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import { ContextStore } from '../store/contextStore';
import DegreeSlider from '../trips/TripBuilder/DegreeSlider';
import { useIsWidthUp } from '../utils';
import { checkFileType } from '../utils/file';
import fileToSha1String from '../utils/file_to_sha1';

const useStyles = makeStyles((theme) => ({
    root: {},
    tabButton: {
        borderRadius: '30px',
        textTransform: 'capitalize',
        fontWeight: 'normal',
    },
    dragHandle: {
        '&:hover': {
            cursor: 'pointer',
        },
    },
    dragPreview: {
        background: '#e6f2ff',
        border: '1px solid #b3d7ff',
        borderRadius: '5px',
    },
    arrowButton: {
        padding: '0.5rem',
        border: `solid 1px ${grey[400]}`,
        borderRadius: '5px',
        cursor: 'pointer',
        background: 'transparent',
        color: theme.palette.primary.main,
        [theme.breakpoints.down('sm')]: {
            padding: '0.25rem',
        },
    },
    labelContainer: {
        backgroundColor: '#EEEEEE',
        display: 'flex',
        justifyContent: 'space-between',
        borderRadius: '8px',
        borderBottom: 'solid #747475 1px',
        margin: '0.25rem',
        width: '127px',
        padding: '0.35rem 0.5rem',
        [theme.breakpoints.down('sm')]: {
            width: '80px',
        },
    },
    inputIcon: {
        color: '#999999',
        fontSize: '16px !important',
        transform: 'translateY(4px)'
    },
    editModal: {
        position: 'fixed',
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        width: '350px',
        borderRadius: '5px',
        padding: theme.spacing(2),
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        zIndex: 10000,
        maxHeight: '80vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        marginTop: '1rem',
    },
}));

const DND_DOCUMENT = 'activity_doc';

const RenderNameLabel = ({ label }) => {
    const isSmallScreen = !useIsWidthUp('sm')
    // text overflow on small screens works fine
    let max_label_length = isSmallScreen ? 100 : 20;
    let label_string = label?.slice(0, max_label_length) + (label?.length > max_label_length ? '...' : '')
    const classes = useStyles();
    return <Tooltip title={label?.length > max_label_length ? label : ''}>
        <Grid className={classes.labelContainer} style={{ width: isSmallScreen ? 'calc(100vw - 13rem)' : '200px' }}>
            <Typography noWrap={true}>
                {label_string}
            </Typography>
        </Grid>
    </Tooltip>
}

const RenderDocumentLabel = ({ label, link, is360 }) => {
    const isSmallScreen = !useIsWidthUp('sm')
    let file_ext = label
    let max_label_length = isSmallScreen ? 14 : 14;
    let label_string = label?.slice(0, max_label_length) + (label?.length > max_label_length && '...')

    const classes = useStyles();
    if (label) {
        let ext = label.split('.').pop()
        file_ext = ext === label ? null : ext;
    }
    const iconMap = {
        'image': is360 ? <AddPhotoAlternateIcon className={classes.inputIcon} /> : <ImageIcon className={classes.inputIcon} />,
        'audio': <VolumeUpIcon className={classes.inputIcon} />,
        'video': <VideoCallIcon className={classes.inputIcon} />,
        'document': <ArticleIcon className={classes.inputIcon} />,
        'link': <LinkIcon className={classes.inputIcon} />,
    };

    const IconComponent = checkFileType(label) ? iconMap[checkFileType(label)] : null;

    return <Tooltip title={label?.length > max_label_length ? label : ''}>
        <Grid className={classes.labelContainer} style={{ width: isSmallScreen ? '200px' : '200px' }}>
            <Link href={link} target="_blank">
                <Typography noWrap={true}>
                    {label_string}
                    {file_ext}
                </Typography>
            </Link>
            {IconComponent && IconComponent}
        </Grid>
    </Tooltip>
}

const DocumentEditor = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm } = React.useContext(ContextStore);
    const [syncActive, setSyncActive] = React.useState(props.data._uploading);
    const [dirty, setDirty] = React.useState(false);
    const [data, setData] = React.useState(props.data);
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();
    const NEW = 0;
    const UPLOADING = 1;
    const READY = 2;
    const state = data.document.file && props.syncActive ? UPLOADING : data.document.link && !data._replace ? READY : NEW;
    const dndref = React.useRef(null);
    const nameref = React.useRef(null);
    const canSave = !!data.document.link || !!data.document.file;
    const isSmallScreen = !useIsWidthUp('sm')
    const [EditDegreeOffset, setEditDegreeOffset] = React.useState(false);

    const getUpdate = () => ({
        ...data,
        file: data.document.file,
        name: data.document.name,
    });
    const onDrop = React.useCallback(
        async (acceptedFiles) => {
            let hash_string = await fileToSha1String(acceptedFiles[0]);
            if (props.documentsAndLinks.filter((doc) => doc?.document?.content_hash === hash_string).length > 0) {
                enqueueSnackbar('Duplicate file found, please check existing attachments', { variant: 'default', autoHideDuration: 3000 });
            } else {

                setData((prevState) => ({
                    ...prevState,
                    document: {
                        ...prevState.document,
                        file: acceptedFiles[0],
                        name: !prevState.name || (prevState.file && prevState.name === prevState.file.name) ? acceptedFiles[0].name : prevState.name,
                        content_hash: hash_string,
                    },
                    _replace: true,
                }));
                setDirty(true);
                nameref.current && nameref.current.focus();
            }
        },
        [data, props.documentsAndLinks]
    );


    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop,
        multiple: false,
    });

    const onChange = (ev) => {
        setData({
            ...data,
            document: {
                ...data.document,
                [ev.target.name]: ev.target.value,
            },
        });
        setDirty(true);
    };

    const on360DegreeChange = (ev) => {
        setData({
            ...data,
            document: {
                ...data.document,
                is_360_degree: ev.target.checked,
            },
        });
        setDirty(true);
    }

    const onSave = (ev) => {
        setSyncActive(true);
        const payload = { ...getUpdate(), ...props.resourceMeta };
        props.onChange && props.onChange({ _uploading: true }, props.data._idx);

        const params = {
            id: payload.id,
            data: payload,
            urlSuffix: !!payload.file ? 'upload/' : null,
            upload: !!payload.file,
        };
        return dataAPI(payload.file ? UPLOAD : UPDATE, props.resource, params)
            .then((res) => {
                if (res.statusCode === 200 || res.statusCode === 201) {
                    enqueueSnackbar('Document saved', { variant: 'success' });
                    const upd = { ...res.data, _uploading: false, _replace: false };
                    setData((prevState) => ({ ...prevState, ...upd }));
                    setDirty(false);
                    props.onChange && props.onChange(upd, props.data._idx);
                } else {
                    enqueueSnackbar('Error saving document: ' + res.statusCode, { variant: 'error', autoHideDuration: 5000 });
                }
            })
            .finally(() => setSyncActive(false));
    };

    const onDocumentDeleted = () => {
        enqueueSnackbar('Document deleted', { variant: 'default' });
        setSyncActive(false);
        props.onDeleted && props.onDeleted(props.idx);
    };

    const onDelete = () => {
        const payload = getUpdate();
        if (payload.id) {
            setSyncActive(true);
            return dataAPI(DELETE, props.resource, { ...props.resourceMeta, id: payload.id })
                .then((res) => {
                    onDocumentDeleted();
                })
                .finally(() => setSyncActive(false));
        } else {
            onDocumentDeleted(props.idx);
        }
    };

    const handleCancelEdit = () => {
        setData({
            ...props.data,
            document: { ...props.data.document },
        });
        setDirty(false);
        setEditDegreeOffset(false);
    };

    const getFileName = () => {
        if (data.document.link) {
            const url = new URL(data.document.link);
            if (url.pathname.lastIndexOf('/') >= 0) {
                const chunks = url.pathname.split('/');
                return chunks[chunks.length - 1];
            } else {
                return url.pathname;
            }
        }
    };

    const onKeyPress = (ev) => {
        if (ev.keyCode === 13) {
            onSave();
        }
    };

    const [, drop] = useDrop({
        accept: DND_DOCUMENT,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover: (item, monitor) => {
            if (!dndref.current) {
                return;
            }
            const dragIndex = item.idx;
            const hoverIndex = props.idx;
            if (dragIndex === hoverIndex) {
                return;
            }
            const hoverBoundingRect = dndref.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            if (props.swapItems) {
                props.swapItems(dragIndex, hoverIndex);
                item.idx = hoverIndex;
            }
        },
        drop: (item, monitor) => {
            props.saveOrder && props.saveOrder();
        },
    });

    const [, drag] = useDrag({
        type: DND_DOCUMENT,
        item: () => {
            return { id: data.id, idx: props.idx };
        },
        collect: (monitor, a) => {
            return {
                isDragging: monitor.isDragging(),
            };
        },
        end: (item, monitor) => {
            props.saveOrder && props.saveOrder();
        },
    });

    drag(drop(dndref));

    const handleOnReplace = (dz) => {
        if (data.document.name) {
            setData({
                ...data,
                _replace: true,
            });
        }

        dz.open();
    };

    const handleSwapItems = (idx, direction) => {
        if (direction === 'Down' && props.documentsAndLinks.length - 1 > idx) {
            props.swapItems(props.idx, props.idx + 1);
        }
        if (direction === 'Up' && idx > 0) {
            props.swapItems(props.idx, props.idx - 1);
        }
    };

    const handleDegreeSliderChange = (newValue) => {
        setData({
            ...data,
            document: {
                ...data.document,
                offset_360_degree: newValue,
            },
        });
        setDirty(true);
    }

    return (
        <>
            <FixedLinearProgress visibility={syncActive} />
            <Grid container direction="row" alignItems="center" my={2}>
                <Grid item container alignItems="center" width={isSmallScreen ? 85 : 100} justifyContent={'flex-start'}>
                    {hasPerm('activity', 'write') && (
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button className={classes.arrowButton} style={{ color: props.idx === 0 && grey[400], cursor: props.idx === 0 && 'default' }} onClick={() => handleSwapItems(props.idx, 'Up')}>
                                <ArrowUpwardIcon />
                            </button>
                            <button className={classes.arrowButton} style={{ color: props.idx === props.documentsAndLinks.length - 1 && grey[400], cursor: props.idx === props.documentsAndLinks.length - 1 && 'default' }} onClick={() => handleSwapItems(props.idx, 'Down')}>
                                <ArrowDownwardIcon />
                            </button>
                        </div>
                    )}
                </Grid>
                {state === NEW || state === READY ? (
                    <>
                        <input {...dz.getInputProps()} />
                        {data.document.file ? (
                            <Grid item xs={8} sm={'auto'}>
                                Pending upload: <RenderNameLabel label={data.document?.file?.name} />
                            </Grid>
                        ) : null}
                        {data.document.name && data.document.id && !data.document.file ? (
                            <Grid item xs={8} sm={'auto'}>
                                <RenderDocumentLabel label={getFileName()} link={data.document.link} is360={data.document.is_360_degree} />
                            </Grid>
                        ) : null}
                        <Grid item xs={12} sm={'auto'} minWidth={isSmallScreen ? 0 : 400} container alignItems="center" my={0.5}>
                            <Grid item xs={5} sm={4} pl={isSmallScreen ? 0 : 1}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    disabled={!hasPerm('activity', 'write')}
                                    onClick={() => {
                                        handleOnReplace(dz);
                                    }}
                                >
                                    {data.document.file || (data.document.name && data.document.id) ? <ReplaceIcon /> : <PublishIcon />}&nbsp;{data.document.file || (data.document.name && data.document.id) ? 'Replace' : 'Choose file'}
                                </Button>
                            </Grid>
                            {checkFileType(getFileName()) === 'image' &&
                                <Grid item xs={12} sm={8} flexWrap={"nowrap"} container minWidth={isSmallScreen ? 0 : 200} style={{ position: 'relative' }}>
                                    <Grid item xs={12} sm={'auto'}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={!!data.document?.is_360_degree}
                                                    onChange={on360DegreeChange}
                                                />
                                            }
                                            label="360 degree image"
                                        />
                                    </Grid>
                                    <Grid item xs={10} sm={'auto'}>
                                        <Button variant="outlined" onClick={() => setEditDegreeOffset(true)}>Degree offset<EditIcon sx={{ fontSize: '16px', marginLeft: '0.25rem', marginBottom: "0.25rem" }} /></Button>
                                    </Grid>
                                    {EditDegreeOffset && (
                                        <div className={classes.editModal}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mb: 2.5 }}>
                                                <IconButton
                                                    aria-label="close"
                                                    size="small"
                                                    onClick={() => setEditDegreeOffset(false)}
                                                    sx={{
                                                        color: 'rgba(0, 0, 0, 0.87)',
                                                        '& .MuiSvgIcon-root': { color: 'rgba(0, 0, 0, 0.87)' },
                                                    }}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            </Box>
                                            <DegreeSlider data={data.document} onChange={handleDegreeSliderChange} />
                                        </div>
                                    )}
                                </Grid>}
                        </Grid>
                        {state === READY || state === UPLOADING ? (
                            <>
                                {state === UPLOADING ? <Typography>Please wait...</Typography> : null}
                            </>
                        ) : null}
                        <Grid item xs={12} mt={1} container direction="row" spacing={1} alignItems="center" sx={{ alignSelf: 'flex-start', width: '100%' }}>
                            {(data.document.file || data.document.id) && (
                                <Grid item xs={3} sm={5} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <TextField
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        label="Display Name"
                                        name="name"
                                        value={data.document.name}
                                        onChange={onChange}
                                        onKeyDown={onKeyPress}
                                        inputRef={nameref}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm="auto" ml={data.document.file || data.document.id ? 2 : 0} sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormActionButtons
                                    mode="edit"
                                    onSubmit={onSave}
                                    submitDisabled={!data.document.name || (data.id && (!canSave || !dirty || syncActive || !hasPerm('activity', 'write')))}
                                    onCancel={handleCancelEdit}
                                    cancelDisabled={!dirty && !data.document.file}
                                    leadingActions={
                                        <DeleteIconButton perm="activity.write" onClick={onDelete} />
                                    }
                                />
                            </Grid>
                        </Grid>
                    </>
                ) : null}

            </Grid>
        </>
    );
};

export default DocumentEditor;
